import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { oidcUserManager } from '../../services/oidcService';
import { getDesktopAssertion } from '../../services/desktopAssertion';
import { TranslationKey } from '../../i18n/translationKeys';
import { Button } from '../../ui/Button/Button';

interface SigninErrorState {
    reason?: 'no-company-access';
    // Acomba Desktop assertion, carried through from a failed desktop login so retry can re-enter
    // the desktop SSO flow instead of the AcombaGo tenant picker.
    src?: string;
}

export const SigninError: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const state = location.state as SigninErrorState | null;
    const reason = state?.reason;
    const src = state?.src;

    const messageKey = reason === 'no-company-access'
        ? TranslationKey.Auth.NoCompanyAccess
        : TranslationKey.Auth.SigninError;

    const handleRetry = () => {
        // A desktop launch is bound to a single workspace via the assertion. Re-carry it in the OIDC
        // state (as DesktopAuth does) so /signin-callback resumes the desktop flow; without it, the
        // callback would fall back to the AcombaGo tenant list and land on the company picker.
        // Prefer the assertion from router state, but fall back to the one persisted at launch: an
        // unexpected error (e.g. the OIDC callback threw) can reach this page without router state,
        // and a plain redirect would wrongly send an Acomba Desktop user to the tenant picker.
        const assertion = src ?? getDesktopAssertion() ?? undefined;
        void oidcUserManager.signinRedirect(assertion !== undefined ? { state: { src: assertion } } : undefined);
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-l">
            <p className="text-primary-600" data-testid="signin-error-message">{t(messageKey)}</p>
            <Button
                type="button"
                data-testid="signin-error-retry-btn"
                onClick={handleRetry}
            >
                {t(TranslationKey.Auth.Retry)}
            </Button>
        </div>
    );
};
